import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import type { NotedOnId } from './CommentsSection';

/**
 * The host queries that read each parent's `notes` list. Bills are left out: `BillNotes` reads its
 * notes from the bill store (accounting).
 */
export const NOTE_HOST_QUERIES = {
  salesOrderId: ['SalesOrderNotesQuery', 'PickTicketNotesQuery'],
  purchaseOrderId: ['PurchaseOrderNotesQuery'],
  buySellOrderId: ['BuySellOrderNotesQuery'],
  workOrderId: ['GrowerProductNotesQuery', 'GrowerProductReceivingTicketNotesQuery'],
  productionRunId: ['ProductionRunNotesQuery'],
  lotId: ['LotNotesQuery'],
  billId: [],
} as const satisfies Record<keyof NotedOnId, readonly string[]>;

const PARENT_KEYS = Object.keys(NOTE_HOST_QUERIES) as Array<keyof NotedOnId>;

export function noteHostQueryNames(parent: NotedOnId | undefined): ReadonlySet<string> {
  if (!parent) return new Set();
  return new Set(PARENT_KEYS.filter((key) => typeof parent[key] === 'number').flatMap((key) => NOTE_HOST_QUERIES[key]));
}

/**
 * After a note is written somewhere other than a comments section (a flag, cancel or revert note),
 * refetches the mounted host notes queries of that note's parent so the note appears in the cards.
 * Only active queries with those operation names are refetched; any other mounted host is left alone.
 */
export function useRefetchNoteHosts() {
  const client = useApolloClient();

  return useCallback(
    async (parent: NotedOnId | undefined) => {
      const names = noteHostQueryNames(parent);
      if (names.size === 0) return;
      // A failed refetch already lands on the host query's own error state (the notes card's Retry).
      await Promise.resolve(
        client.refetchQueries({ include: 'active', onQueryUpdated: (query) => names.has(query.queryName ?? '') })
      ).catch(() => undefined);
    },
    [client]
  );
}
